import React, { useState, useEffect, useRef } from 'react';
import { useMsal } from "@azure/msal-react";
import './WriteFlights.css'; // We'll create this for styling
import Panel from './Panel';
import { getTemplateName } from '../utils/templateValidation';
import { authFetch, isInteractiveAuthError } from '../authFetch';

// Templates will be fetched from backend API

function WriteFlights({ edit = false, submission = null, selectedTemplate = null, parentSubmissionId = null, parentFieldId = null }) {
  const { accounts } = useMsal();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedTemplateVersion, setSelectedTemplateVersion] = useState(1);
  const [currentFormDefinition, setCurrentFormDefinition] = useState([]);
  const [formData, setFormData] = useState({});
  const [editingUuid, setEditingUuid] = useState('');
  const [childTemplateRefs, setChildTemplateRefs] = useState([]); // Track child template submissions
  const [loadedSubmission, setLoadedSubmission] = useState(null);
  const fetchingSubmissionRef = useRef(null); // Track which submission is currently being fetched

  // Fetch templates on mount - get only latest versions for dropdown
  useEffect(() => {
    const TEMPLATES_API_ENDPOINT = process.env.REACT_APP_TEMPLATES_API_ENDPOINT ||
      'https://68ysvrjdb7.execute-api.eu-west-2.amazonaws.com/formTemplates-dev';

    const fetchTemplates = async () => {
      try {
        const apiUrl = `${TEMPLATES_API_ENDPOINT}?latestOnly=true`;
        const res = await authFetch(apiUrl);
        
        if (!res.ok) {
          return;
        }
        const data = await res.json();
        
        // Expecting data to be array, each row may contain template object inside payload/template key
        const normalised = data.map(item => {
          const template = item.template || item.payload?.template || item;
          return {
            ...template,
            version: item.version || template.version || 1,
            templateId: item.templateId || template.templateId || null, // Preserve backend UUID
            archived: item.archived || template.archived || false // Backend returns archived status
          };
        });
        setTemplates(normalised);
        console.log('[WriteFlights] Fetched templates:', normalised.map(t => ({ id: t.id, name: t.name, archived: t.archived })));
      } catch (err) {
        // Error loading templates - fail silently
      }
    };

    fetchTemplates();
  }, []);

  // Handle URL query parameters - fetch submission if in edit mode
  useEffect(() => {
    if (templates.length === 0) return; // Wait for templates to load
    
    // With HashRouter, query params are in the hash, not window.location.search
    const hash = window.location.hash;
    const queryStart = hash.indexOf('?');
    console.log('[WriteFlights] URL check:', { hash, hasQuery: queryStart !== -1 });
    if (queryStart === -1) return;
    
    const params = new URLSearchParams(hash.substring(queryStart + 1));
    const editParam = params.get('edit');
    const submissionIdParam = params.get('submissionId');
    const templateIdParam = params.get('templateId');
    const parentFieldIdParam = params.get('parentFieldId');
    
    console.log('[WriteFlights] URL params:', { editParam, submissionIdParam, templateIdParam, parentFieldIdParam });
    
    // Handle edit mode - fetch submission data
    if (editParam === 'true' && submissionIdParam) {
      // Prevent duplicate fetches using both state and ref
      if (loadedSubmission && (loadedSubmission.submissionId === submissionIdParam || loadedSubmission.uuid === submissionIdParam)) {
        console.log('[WriteFlights] Already loaded this submission, skipping fetch');
        return;
      }
      
      if (fetchingSubmissionRef.current === submissionIdParam) {
        console.log('[WriteFlights] Already fetching this submission, skipping duplicate fetch');
        return;
      }
      
      console.log('[WriteFlights] Fetching submission from URL params:', submissionIdParam);
      fetchingSubmissionRef.current = submissionIdParam;
      
      const READ_FLIGHTS_API_ENDPOINT = process.env.REACT_APP_READ_FLIGHTS_API_ENDPOINT ||
        'https://whzy23elj2.execute-api.eu-west-2.amazonaws.com/prod/readFlightData';
      
      authFetch(`${READ_FLIGHTS_API_ENDPOINT}?uuid=${submissionIdParam}`)
        .then(res => res.json())
        .then(data => {
          console.log('[WriteFlights] Fetched submission data:', data);
          // Handle both paginated response (with items array) and direct array response
          const items = data.items || data;
          if (items && items.length > 0) {
            // Find the submission that matches the requested UUID
            const sub = items.find(item => 
              item.submissionId === submissionIdParam || 
              item.uuid === submissionIdParam
            );
            
            if (sub) {
              console.log('[WriteFlights] Found matching submission:', sub);
              setLoadedSubmission(sub);
              fetchingSubmissionRef.current = null;
            } else {
              console.warn('[WriteFlights] No submission found matching UUID:', submissionIdParam);
              console.warn('[WriteFlights] Available items:', items.map(i => ({ uuid: i.uuid, submissionId: i.submissionId })));
              fetchingSubmissionRef.current = null;
            }
          } else {
            console.warn('[WriteFlights] No submission found in response');
            fetchingSubmissionRef.current = null;
          }
        })
        .catch(err => {
          console.error('Error loading submission:', err);
          fetchingSubmissionRef.current = null;
        });
    }
    // Handle child template creation mode - auto-select the template
    else if (templateIdParam && parentFieldIdParam && !selectedTemplateId) {
      setSelectedTemplateId(templateIdParam);
    }
  }, [templates, selectedTemplateId, loadedSubmission]);

  // Auto-select template if passed via prop (for child template creation)
  useEffect(() => {
    if (selectedTemplate && !selectedTemplateId) {
      console.log('🔗 Auto-selecting template for child submission:', selectedTemplate);
      setSelectedTemplateId(selectedTemplate);
    }
  }, [selectedTemplate, selectedTemplateId]);

  // Effect to update currentFormDefinition when selectedTemplateId changes
  useEffect(() => {
    if (selectedTemplateId) {
      const template = templates.find(t => t.id === selectedTemplateId);
      if (template) {
        setCurrentFormDefinition(template.definition || []);
        setSelectedTemplateVersion(template.version || 1);
        
        // Initialize child template references from template definition
        // BUT: In edit mode, preserve existing child refs from the submission
        const activeSubmission = submission || loadedSubmission;
        const isEditMode = edit || (loadedSubmission !== null);
        
        if (isEditMode && activeSubmission) {
          // In edit mode, check if submission has existing child template refs
          const existingChildRefs = activeSubmission.childTemplateRefs || 
                                    activeSubmission.formData?.childTemplateRefs;
          
          if (existingChildRefs && existingChildRefs.length > 0) {
            console.log('[WriteFlights] Preserving existing childTemplateRefs from submission:', existingChildRefs);
            setChildTemplateRefs(existingChildRefs);
          } else {
            // No existing child refs in submission, initialize from template
            const childRefs = [];
            (template.definition || []).forEach(field => {
              if (field.type === 'template-reference' && field.templateId) {
                childRefs.push({
                  fieldId: field.id,
                  templateId: field.templateId,
                  submissionId: null,
                  required: field.required || false,
                  status: 'pending'
                });
              }
            });
            setChildTemplateRefs(childRefs);
          }
        } else {
          // Not in edit mode - initialize fresh child refs from template
          const childRefs = [];
          (template.definition || []).forEach(field => {
            if (field.type === 'template-reference' && field.templateId) {
              childRefs.push({
                fieldId: field.id,
                templateId: field.templateId,
                submissionId: null,
                required: field.required || false,
                status: 'pending'
              });
            }
          });
          setChildTemplateRefs(childRefs);
        }
      } else {
        setCurrentFormDefinition([]);
        setSelectedTemplateVersion(1);
        setChildTemplateRefs([]);
      }
    } else {
      setCurrentFormDefinition([]);
      setSelectedTemplateVersion(1);
      setChildTemplateRefs([]);
    }
    // Only reset form data if NOT in edit mode (edit mode will populate it separately)
    if (!edit && !loadedSubmission) {
      setFormData({});
    }
  }, [selectedTemplateId, templates, edit, loadedSubmission, submission]);

  // Helper to slugify labels into ids for fallback definitions
  const slugify = (s='') => s.toString().toLowerCase().trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-');

  // When in edit mode, resolve template and prefill values after templates are loaded
  useEffect(() => {
    // Use either prop-based submission or URL-loaded submission
    const activeSubmission = submission || loadedSubmission;
    const isEditMode = edit || (loadedSubmission !== null);
    
    console.log('[WriteFlights] Edit mode check:', { 
      isEditMode, 
      hasActiveSubmission: !!activeSubmission, 
      templatesCount: templates.length,
      activeSubmission 
    });
    
    if (!isEditMode || !activeSubmission || templates.length === 0) {
      return;
    }
    
    const uuid = activeSubmission.submissionId || activeSubmission.uuid || '';
    setEditingUuid(uuid);

    // Try to get template info from multiple possible locations
    // Some submissions have it at top level, others in formData
    let subTemplateSlug = activeSubmission.templateSlug || activeSubmission.formData?.templateSlug || '';
    let subTemplateId = activeSubmission.templateId || activeSubmission.formData?.templateId || '';
    let subTemplateVersion = activeSubmission.templateVersion || activeSubmission.formData?.templateVersion || 1;
    
    console.log('[WriteFlights] Template lookup:', { 
      subTemplateSlug, 
      subTemplateId, 
      subTemplateVersion,
      hasFormData: !!activeSubmission.formData,
      formDataKeys: activeSubmission.formData ? Object.keys(activeSubmission.formData) : [] 
    });
    
    // If we have a slug, use it directly
    if (subTemplateSlug) {
      const templateBySlug = templates.find(t => t.id === subTemplateSlug);
      console.log('[WriteFlights] Template by slug:', templateBySlug);
      if (templateBySlug) {
        // Debug: Check the definition around the weight section
        const weightFields = templateBySlug.definition.filter(f => 
          f.label && (f.label.includes('weight') || f.label.includes('CoG') || f.label.includes('Ballasting'))
        );
        console.log('[WriteFlights] Weight-related fields in template:', weightFields);

        setSelectedTemplateId(subTemplateSlug);
        setSelectedTemplateVersion(subTemplateVersion);
        setCurrentFormDefinition(templateBySlug.definition || []); 
        return;
      }
    }
    
    // If we have UUID but no slug, try to find the template by UUID
    if (!subTemplateSlug && subTemplateId) {
      const templateByUuid = templates.find(t => t.templateId === subTemplateId);
      console.log('[WriteFlights] Template by UUID:', templateByUuid);
      if (templateByUuid) {
        setSelectedTemplateId(templateByUuid.id); // Use slug from template
        setSelectedTemplateVersion(subTemplateVersion);
        setCurrentFormDefinition(templateByUuid.definition || []);
        return;
      }
    }
    
    if (!subTemplateSlug && !subTemplateId) {
      // Fallback match by name/title
      const byName = templates.find(t => (t.name || '').toLowerCase() === (activeSubmission.formTitle || '').toLowerCase());
      if (byName) {
        setSelectedTemplateId(byName.id);
        setSelectedTemplateVersion(byName.version || 1);
        setCurrentFormDefinition(byName.definition || []);
        return;
      }
      
      // Last resort: derive form definition from sections
      if (Array.isArray(activeSubmission.sections) && activeSubmission.sections.length > 0) {
        const derivedDef = [];
        activeSubmission.sections.forEach((sec, si) => {
          derivedDef.push({ id: `section-${si}`, type: 'section', label: sec.sectionLabel || `Section ${si+1}` });
          (sec.fields || []).forEach((f, fi) => {
            derivedDef.push({ id: slugify(`${si}-${f.label || 'field'}-${fi}`), type: 'text', label: f.label || `Field ${fi+1}`, defaultValue: f.value || '' });
          });
        });
        setCurrentFormDefinition(derivedDef);
        const initialData = {};
        derivedDef.forEach(d => { if (d.type !== 'section') initialData[d.id] = d.defaultValue || ''; });
        setFormData(initialData);
      }
    }
  }, [edit, submission, loadedSubmission, templates]);

  // After currentFormDefinition is available in edit mode, map values from submission to formData
  useEffect(() => {
    const activeSubmission = submission || loadedSubmission;
    const isEditMode = edit || (loadedSubmission !== null);
    
    console.log('[WriteFlights] Form data population check:', {
      isEditMode,
      hasActiveSubmission: !!activeSubmission,
      formDefinitionLength: currentFormDefinition.length,
      submissionStructure: activeSubmission ? {
        hasFormData: !!activeSubmission.formData,
        hasSections: !!activeSubmission.sections,
        formDataKeys: activeSubmission.formData ? Object.keys(activeSubmission.formData) : [],
        sectionsCount: activeSubmission.sections?.length,
        fullSubmission: activeSubmission
      } : null
    });
    
    if (!isEditMode || !activeSubmission || currentFormDefinition.length === 0) {
      return;
    }

    // Check if formData contains nested structure (for child submissions)
    let dataSource = activeSubmission;
    if (activeSubmission.formData && typeof activeSubmission.formData === 'object') {
      // Check if formData has sections (nested structure for child submissions)
      if (activeSubmission.formData.sections) {
        console.log('[WriteFlights] Using nested formData.sections structure');
        dataSource = activeSubmission.formData;
      }
    }

    // Build label queues as a fallback (preserves order for duplicate labels)
    const labelQueues = new Map();
    if (Array.isArray(dataSource.sections)) {
      dataSource.sections.forEach(sec => {
        (sec.fields || []).forEach(f => {
          const k = (f && typeof f.label === 'string') ? f.label : '';
          if (!labelQueues.has(k)) labelQueues.set(k, []);
          labelQueues.get(k).push(f ? f.value : undefined);
        });
      });
    }
    
    console.log('[WriteFlights] Label queues built:', Array.from(labelQueues.entries()));

    const coerce = (field, v) => {
      if (v === undefined || v === null) return v;
      switch (field.type) {
        case 'checkbox':
          if (typeof v === 'boolean') return v;
          if (typeof v === 'string') return v.toLowerCase() === 'true';
          return Boolean(v);
        default:
          return v;
      }
    };

    const initialData = {};
    let sectionIdx = -1;
    let fieldIdxInSection = 0;

    currentFormDefinition.forEach(field => {
      if (field.type === 'section') {
        sectionIdx += 1;
        fieldIdxInSection = 0;
        return;
      }

      // Skip description fields for data population - they don't store values
      if (field.type === 'description') {
        return;
      }

      let value;

      // 1) If submission.formData is id-keyed (rare), prefer it
      if (
        dataSource.formData &&
        typeof dataSource.formData === 'object' &&
        Object.prototype.hasOwnProperty.call(dataSource.formData, field.id)
      ) {
        value = dataSource.formData[field.id];
      }

      // 2) Index-based mapping against submission.sections to avoid duplicate-label collisions
      if (value === undefined && Array.isArray(dataSource.sections)) {
        const idx = sectionIdx < 0 ? 0 : sectionIdx; // fields before any section belong to section 0 (General)
        value = dataSource.sections?.[idx]?.fields?.[fieldIdxInSection]?.value;
      }

      // 3) Fallback to label queues (consume in order)
      if (value === undefined && labelQueues.has(field.label)) {
        const q = labelQueues.get(field.label);
        value = q.length ? q.shift() : undefined;
      }

      // 4) Defaults
      if (value === undefined) {
        if (field.type === 'select' && field.options && field.options.length > 0) {
          value = field.options[0]?.value ?? '';
        } else if (field.type === 'checkbox') {
          value = field.defaultValue ?? false;
        } else {
          value = field.defaultValue ?? '';
        }
      }

      initialData[field.id] = coerce(field, value);
      fieldIdxInSection += 1;
    });

    console.log('[WriteFlights] Populated form data:', initialData);
    
    // Correction mechanism: If description fields have values, it means data was saved with the old buggy index
    // We need to shift values from description fields to the next available input field
    const correctedData = { ...initialData };
    const descriptionFieldsWithValues = [];
    
    currentFormDefinition.forEach(field => {
      if (field.type === 'description' && correctedData[field.id]) {
        descriptionFieldsWithValues.push({ 
          fieldId: field.id, 
          value: correctedData[field.id],
          label: field.label 
        });
      }
    });
    
    if (descriptionFieldsWithValues.length > 0) {
      console.warn('[WriteFlights] Found values in description fields (data migration needed):', descriptionFieldsWithValues);
      
      // For each description field with a value, find the next input field and move the value there
      currentFormDefinition.forEach((field, idx) => {
        if (field.type === 'description' && correctedData[field.id]) {
          const valueToMove = correctedData[field.id];
          
          // Find the next input field after this description
          for (let i = idx + 1; i < currentFormDefinition.length; i++) {
            const nextField = currentFormDefinition[i];
            if (nextField.type !== 'section' && nextField.type !== 'description') {
              // Check if this field is empty or has a default value
              if (!correctedData[nextField.id] || correctedData[nextField.id] === '' || correctedData[nextField.id] === false) {
                console.log(`[WriteFlights] Migrating value "${valueToMove}" from description field to ${nextField.label}`);
                correctedData[nextField.id] = valueToMove;
                delete correctedData[field.id]; // Remove from description field
                break;
              }
            }
          }
        }
      });
    }
    
    setFormData(correctedData);
  }, [edit, submission, loadedSubmission, currentFormDefinition]);

  // Initialize/Reset form data based on currentFormDefinition and load from localStorage
  useEffect(() => {
    console.log('[WriteFlights] localStorage effect triggered:', { 
      edit, 
      selectedTemplateId, 
      formDefinitionLength: currentFormDefinition.length,
      willReturn: !!edit 
    });
    if (edit) {
      console.log('[WriteFlights] Skipping localStorage initialization (edit mode)');
      return; // don't override edit prefill
    }
    console.log('[WriteFlights] Proceeding with localStorage initialization');
    const initialData = {};
    // Only proceed if a template is selected and its definition is available
    if (selectedTemplateId && currentFormDefinition && currentFormDefinition.length > 0) {
      const localStorageKey = `formData-${selectedTemplateId}`;
      let savedData = null;
      try {
        const savedDataString = localStorage.getItem(localStorageKey);
        if (savedDataString) {
          savedData = JSON.parse(savedDataString);
        }
      } catch (error) {
        // Error parsing saved form data from localStorage - fail silently
        // Optionally, clear the corrupted data if parsing fails
        // localStorage.removeItem(localStorageKey);
      }

      currentFormDefinition.forEach(field => {
        // Use saved data if available and the field exists in it
        if (savedData && savedData.hasOwnProperty(field.id)) {
          initialData[field.id] = savedData[field.id];
        } else {
          // Otherwise, use default values
          if (field.type === 'select' && field.options && field.options.length > 0) {
            initialData[field.id] = field.options[0]?.value || '';
          } else if (field.type === 'checkbox') {
            initialData[field.id] = field.defaultValue || false;
          } else {
            initialData[field.id] = field.defaultValue || '';
          }
        }
      });
    }
    setFormData(initialData);
  }, [currentFormDefinition, selectedTemplateId, edit]); // Added selectedTemplateId to dependencies

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevData => {
      const updatedData = {
        ...prevData,
        [name]: type === 'checkbox' ? checked : value
      };
      // Save to localStorage
  if (!edit && selectedTemplateId) {
        const localStorageKey = `formData-${selectedTemplateId}`;
        try {
          localStorage.setItem(localStorageKey, JSON.stringify(updatedData));
        } catch (error) {
          // Error saving form data to localStorage - fail silently
          // Handle potential errors, e.g., localStorage full
        }
      }
      return updatedData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Allow submission even with incomplete child templates
    // Child templates can be completed later through editing

    const WRITE_FLIGHTS_API_ENDPOINT = process.env.REACT_APP_WRITE_FLIGHTS_API_ENDPOINT ||
      'https://ivrm771szg.execute-api.eu-west-2.amazonaws.com/prod/submitFlight';

    const apiUrl = WRITE_FLIGHTS_API_ENDPOINT;

    const selectedTemplate = templates.find(t => t.id === selectedTemplateId);
    const formTitle = selectedTemplate ? selectedTemplate.name : 'Unknown Form';
    const backendTemplateId = selectedTemplate ? selectedTemplate.templateId : null; // Backend UUID
  // In edit mode, preserve original submittedBy if available; else fallback to current user
  const submittedBy = (edit && submission && submission.submittedBy) ? submission.submittedBy : (accounts[0]?.username || 'unknown_user');

    // Transform flat formData into ordered sections with human-readable labels
    const sectionedData = [];
    let currentSection = null;
    currentFormDefinition.forEach(def => {
      if (def.type === 'section') {
        currentSection = { sectionLabel: def.label, fields: [] };
        sectionedData.push(currentSection);
      } else if (def.type === 'description') {
        // Skip description fields - they are display-only and don't need to be saved
        return;
      } else {
        const value = formData[def.id];
        const fieldLabel = def.label;
        if (!currentSection) {
          // If a field appears before any explicit section, create a default section
          currentSection = { sectionLabel: 'General', fields: [] };
          sectionedData.push(currentSection);
        }
        currentSection.fields.push({ label: fieldLabel, value });
      }
    });

  let submissionPayload = {
      formTitle,
      submittedBy,
      templateId: backendTemplateId, // Use backend UUID instead of slug
      templateSlug: selectedTemplateId, // Keep slug for reference
      templateVersion: selectedTemplateVersion,
      sections: sectionedData,
      childTemplateRefs: childTemplateRefs.length > 0 ? childTemplateRefs : undefined,
    };

    // In edit mode, include uuid and a normalized formData map keyed by field id for backend
    if (edit && editingUuid) {
      // For UPDATE, formData should have the same structure as CREATE
      const formDataEnvelope = {
        formTitle,
        submittedBy,
        templateId: backendTemplateId, // Use backend UUID
        templateSlug: selectedTemplateId, // Keep slug for reference
        templateVersion: selectedTemplateVersion,
        sections: sectionedData,
        childTemplateRefs: childTemplateRefs.length > 0 ? childTemplateRefs : undefined,
      };

  submissionPayload = {
        uuid: editingUuid,
        operation: 'UPDATE',
        formData: formDataEnvelope,
        templateId: backendTemplateId, // Use backend UUID
        templateSlug: selectedTemplateId, // Keep slug for reference
        templateVersion: selectedTemplateVersion,
        childTemplateRefs: childTemplateRefs.length > 0 ? childTemplateRefs : undefined,
      };
    }

    // Add parent relationship if this is a child submission (must be after edit mode logic)
    if (parentSubmissionId && parentFieldId) {
      console.log('🟢 [WriteFlights] Adding parent relationship to payload:', {
        parentSubmissionId,
        parentFieldId
      });
      submissionPayload.parentSubmissionId = parentSubmissionId;
      submissionPayload.parentFieldId = parentFieldId;
      console.log('🟢 [WriteFlights] Updated payload:', JSON.stringify(submissionPayload, null, 2));
    }

    console.log('🚀 [WriteFlights] Submitting form:', {
      isEdit: edit,
      hasParentRelationship: !!(parentSubmissionId && parentFieldId),
      parentSubmissionId,
      parentFieldId,
      payload: submissionPayload
    });

    try {
      const response = await authFetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionPayload),
      });
      
      const result = await response.json();

      if (!response.ok) {
        alert(`Error submitting form: ${result.message || 'Unknown error from API. Status: ' + response.status}`);
        return;
      }

      alert(`${result.message || (edit ? 'Form Updated' : '')} ${result.uuid ? `(ID: ${result.uuid})` : ''}`.trim());      // Clear form data and localStorage after successful submission
      const initialData = {};
      if (currentFormDefinition && currentFormDefinition.length > 0) {
        currentFormDefinition.forEach(field => {
          if (field.type === 'select' && field.options && field.options.length > 0) {
            initialData[field.id] = field.options[0]?.value || '';
          } else if (field.type === 'checkbox') {
            initialData[field.id] = field.defaultValue || false;
          } else {
            initialData[field.id] = field.defaultValue || '';
          }
        });
      }
      setFormData(initialData);
      
      // Clear the saved data from localStorage
      if (selectedTemplateId) {
        const localStorageKey = `formData-${selectedTemplateId}`;
        localStorage.removeItem(localStorageKey);
      }

      // If this is a child template submission, notify parent window and close
      const hash = window.location.hash;
      const queryStart = hash.indexOf('?');
      if (queryStart !== -1) {
        const params = new URLSearchParams(hash.substring(queryStart + 1));
        const parentFieldId = params.get('parentFieldId');
        
        if (parentFieldId && window.opener) {
          // Notify parent window of successful submission
          window.opener.postMessage({
            type: 'childTemplateSubmitted',
            fieldId: parentFieldId,
            submissionId: result.uuid,
            templateId: selectedTemplateId
          }, window.location.origin);
          
          // Close the child window after a brief delay
          setTimeout(() => {
            window.close();
          }, 500);
        }
      }

    } catch (error) {
      if (isInteractiveAuthError(error)) return;
      alert(`Error submitting form: ${error.message}`);
    }
  };

  const renderField = (field) => {
    // Debug specific field
    if (field.id === 'fe37e9b8-29bf-4ce3-a9ff-369cd941e940') {
      console.log('[WriteFlights] Rendering weight field:', {
        fieldId: field.id,
        fieldLabel: field.label,
        fieldType: field.type,
        valueInFormData: formData[field.id],
        formDataHasKey: field.id in formData,
        allFormDataKeys: Object.keys(formData).length
      });
    }
    
    switch (field.type) {
      case 'text':
      case 'date':
      case 'email':
      case 'number':
        return (
          <input
            type={field.type}
            id={field.id}
            name={field.id}
            placeholder={field.placeholder || ''}
            value={formData[field.id] || ''}
            onChange={handleChange}
            className="form-input"
            autoComplete="off"
          />
        );
      case 'select':
        return (
          <select
            id={field.id}
            name={field.id}
            value={formData[field.id] || ''}
            onChange={handleChange}
            className="form-select"
            autoComplete="off"
          >
            {(field.options || []).map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      case 'checkbox':
        return (
          <input
            type="checkbox"
            id={field.id}
            name={field.id}
            checked={formData[field.id] || false}
            onChange={handleChange}
            className="form-checkbox"
          />
        );
      case 'textarea':
        return (
          <textarea
            id={field.id}
            name={field.id}
            placeholder={field.placeholder || ''}
            value={formData[field.id] || ''}
            onChange={handleChange}
            rows={field.rows || 3} // Default to 3 rows if not specified
            className="form-textarea"
            autoComplete="off"
          />
        );
      case 'description':
        return (
          <div className="form-description-text">
            {field.label}
          </div>
        );
      case 'template-reference':
        return renderTemplateReference(field);
      // Add cases for other types like 'radio' as needed
      default:
        return <p>Unsupported field type: {field.type}</p>;
    }
  };

  // Render template reference field - simplified to show status only
  const renderTemplateReference = (field) => {
    const ref = childTemplateRefs.find(r => r.fieldId === field.id);
    if (!ref) return <p>Template reference not initialized</p>;
    
    const templateName = getTemplateName(field.templateId, templates);
    const statusIcon = ref.status === 'completed' ? '✅' : '⚪';
    
    return (
      <div className="template-reference-field">
        <div className="template-ref-header">
          <div className="template-ref-info">
            <span className="template-ref-status">{statusIcon}</span>
            <span className="template-ref-name">{templateName}</span>
            {field.required && <span className="required-badge">Required</span>}
          </div>
        </div>
      </div>
    );
  };

  const handleTemplateChange = (e) => {
    setSelectedTemplateId(e.target.value);
  };

  // Debug: Check formData at render time
  useEffect(() => {
    if (selectedTemplateId && currentFormDefinition.length > 0) {
      console.log('[WriteFlights] RENDER - formData state:', {
        formDataKeys: Object.keys(formData),
        sampleValues: {
          'fe37e9b8-29bf-4ce3-a9ff-369cd941e940': formData['fe37e9b8-29bf-4ce3-a9ff-369cd941e940'],
        },
        fullFormData: formData
      });
    }
  });

  return (
    <div className="page write-flights-container">
      <Panel>
        <div className="template-selector-container">
          <label htmlFor="template-select" className="form-label">Select Form Template:</label>
          <select 
            id="template-select" 
            value={selectedTemplateId} 
            onChange={handleTemplateChange} 
            disabled={edit && !!selectedTemplateId}
            className="form-select template-select"
          >
            <option value="">-- Select a Template --</option>
            {templates.filter(t => !t.archived).map(template => (
              <option key={template.id} value={template.id}>
                {template.name} (v{template.version || 1})
              </option>
            ))}
          </select>
          {selectedTemplateId && selectedTemplateVersion && (
            <div className="template-version-info">
              Using template version {selectedTemplateVersion}
            </div>
          )}
        </div>

        {selectedTemplateId && currentFormDefinition.length > 0 && (
          <form onSubmit={handleSubmit} className="dynamic-form" autoComplete="off">
            {currentFormDefinition.map(field => {
              if (field.type === 'section') {
                return <h2 key={field.id} className="form-section-title">{field.label}</h2>;
              }
              if (field.type === 'description') {
                return (
                  <div key={field.id} className="form-field form-field-description">
                    {renderField(field)}
                  </div>
                );
              }
              return (
                <div key={field.id} className={`form-field ${field.type === 'checkbox' ? 'form-field-checkbox' : ''}`}>
                  <label htmlFor={field.id} className="form-label">{field.label}:</label>
                  {renderField(field)}
                </div>
              );
            })}
          <button type="submit" className="submit-button">{edit ? 'Update Form' : 'Submit Form'}</button>
          </form>
        )}
      </Panel>
    </div>
  );
}

export default WriteFlights;
